import React, { useEffect, useState } from 'react';
import { useWalletCashback } from '../../context/WalletCashbackContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PieChartIcon from '@mui/icons-material/PieChart';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CircularProgress } from '@mui/material';

interface CashbackItem {
  id: string;
  amount: number; // Assuming this is in cashback points
  expires_at: string;
}

interface WalletData {
  balance: number; // Assuming this is in EGP
}

interface WalletContext {
  wallet: WalletData | null;
  cashbackList: CashbackItem[];
  loading: boolean;
  refreshCashback: () => Promise<void>;
}

// Conversion rate: 5 cashback points = 1 EGP
const CASHBACK_TO_EGP_RATE = 5;

const WalletOverviewPage: React.FC = () => {
   const { wallet, cashbackList, loading, refreshCashback } = useWalletCashback();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  
  useEffect(() => {
    refreshCashback();
  }, [refreshCashback]);

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    await refreshCashback();
    setIsRefreshing(false);
  };

  // Convert cashback points to EGP
  const convertToEGP = (points: number): number => {
    return points / CASHBACK_TO_EGP_RATE;
  };

  // Calculate statistics in EGP
  const totalCashbackPoints = cashbackList.reduce((sum, cb) => sum +parseInt( cb.amount), 0);
  const totalCashbackEGP = convertToEGP(totalCashbackPoints);
  
  const expiringSoon: number = cashbackList.filter((cb) => {
    const expiresInDays: number = (new Date(cb.expires_at)).getTime() - new Date().getTime();
    return expiresInDays <= 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  }).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ar-EG');
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">محفظتي</h1>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={`flex items-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg ${isRefreshing ? 'opacity-70' : 'hover:bg-blue-100'}`}
        >
          <RefreshIcon className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          تحديث
        </button>
      </div>

      {/* Wallet Balance Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg mb-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm opacity-80">الرصيد المتاح</p>
            {/* <h2 className="text-3xl font-bold mt-1"> {wallet?.balance} EGP</h2> */}
            <h2 className="text-3xl font-bold mt-1"> {totalCashbackEGP} EGP</h2>
          </div>
          <div className="bg-white bg-opacity-20 p-3 rounded-full">
            <AttachMoneyIcon fontSize="medium" />
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="bg-green-100 p-2 rounded-full mr-3">
              <PieChartIcon className="text-green-600" fontSize="small" />
            </div>
            <div>
              <p className="text-sm text-gray-500">إجمالي الكاش باك</p>
              <p className="font-semibold">{totalCashbackEGP.toFixed(2)} EGP</p>
              <p className="text-xs text-gray-400">{totalCashbackPoints} نقطة</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="bg-yellow-100 p-2 rounded-full mr-3">
              <AccessTimeIcon className="text-yellow-600" fontSize="small" />
            </div>
            <div>
              <p className="text-sm text-gray-500">عروض تنتهي قريباً</p>
              <p className="font-semibold">{expiringSoon}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <AttachMoneyIcon className="text-blue-600" fontSize="small" />
            </div>
            <div>
              <p className="text-sm text-gray-500">عدد العروض</p>
              <p className="font-semibold">{cashbackList.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cashback List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-lg text-gray-800">الكاش باك المتاح</h3>
        </div>
        
        {cashbackList.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            لا يوجد كاش باك متاح حالياً
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {cashbackList.map((cb) => {
              const expiresInDays: number = (new Date(cb.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
              const isExpiringSoon: boolean = expiresInDays <= 3;
              const cashbackEGP = convertToEGP(parseInt(cb?.amount));
              
              return (
                <li key={cb.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-full mr-3 ${isExpiringSoon ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                        <AttachMoneyIcon fontSize="small" />
                      </div>
                      <div>
                        <p className="font-medium">{cashbackEGP.toFixed(2)} EGP</p>
                        <p className="text-xs text-primary-dark">{cb.amount} نقطة</p>
                        <p className={`text-sm ${isExpiringSoon ? 'text-red-500' : 'text-gray-500'}`}>
                          {isExpiringSoon ? 'تنتهي قريباً: ' : 'تنتهي في: '}
                          {formatDate(cb.expires_at)}
                        </p>
                      </div>
                    </div>
                    {isExpiringSoon && (
                      <span className="flex items-center px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                        <ErrorOutlineIcon fontSize="small" className="mr-1" />
                        عاجل
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WalletOverviewPage;